import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { awsConfig } from '../aws-config';

const userPool = new CognitoUserPool({
  UserPoolId: awsConfig.userPoolId,
  ClientId: awsConfig.userPoolClientId,
});

/**
 * Thin wrapper around amazon-cognito-identity-js (SRP auth against the
 * admin-created-users-only pool - see dashboard/template.yaml). Deliberately
 * not using full aws-amplify here: Amplify's Auth module pulls in a much
 * larger dependency tree than this dashboard needs for plain
 * username/password login with no federation, MFA, or hosted UI.
 */

export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ user, session }),
      onFailure: (err) => reject(err),
      // Admin-created users get a temporary password (AdminCreateUserConfig
      // in template.yaml) and Cognito forces a change on first login -
      // surface that as a distinct outcome so the login page can render a
      // "set a new password" form instead of a generic error.
      newPasswordRequired: (userAttributes) => {
        // Cognito's newPasswordRequired callback echoes back every
        // required attribute the user already has (email, email_verified),
        // not just ones that still need a value. RespondToAuthChallenge
        // rejects the whole request with "Cannot modify an already
        // provided email" if `email` is resubmitted here - this pathway
        // can only set the new password, not change already-set
        // attributes (email changes go through a separate verified flow).
        // Both have to go, not just email_verified - DashboardUserPool's
        // only Required schema attribute is email, so after both deletes
        // userAttributes is correctly just {}, nothing left to submit.
        delete userAttributes.email_verified;
        delete userAttributes.email;
        resolve({ user, newPasswordRequired: true, userAttributes });
      },
    });
  });
}

export function completeNewPassword(user, newPassword, userAttributes) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => resolve({ user, session }),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Kicks off Cognito's self-service reset flow. Unlike newPasswordRequired
 * (an admin-forced reset mid sign-in, which has to carry a live CognitoUser
 * session object through router state - see SetNewPassword.jsx), this pair
 * is stateless: confirmForgotPassword() below just needs the email again,
 * not the same CognitoUser instance forgotPassword() used. That's what
 * lets ForgotPassword.jsx implement both steps as plain component state on
 * one page instead of a second route.
 *
 * inputVerificationCode is the callback Cognito invokes for the normal
 * email/password-pool path (send a code, wait for it back via
 * confirmPassword) - onSuccess only fires for pool configurations that
 * don't require that round trip, which doesn't apply to DashboardUserPool,
 * but is handled anyway rather than left to hang.
 */
export function forgotPassword(email) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.forgotPassword({
      onSuccess: () => resolve({ codeSent: false }),
      onFailure: (err) => reject(err),
      inputVerificationCode: (data) => {
        resolve({ codeSent: true, destination: data?.CodeDeliveryDetails?.Destination });
      },
    });
  });
}

export function confirmForgotPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }

    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session && session.isValid() ? { user, session } : null);
    });
  });
}

export function signOut() {
  const user = userPool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

/**
 * Self-service password change for an already-signed-in user (the nav's
 * "Change password" option) - distinct from both forgotPassword() (no
 * active session, emails a code) and completeNewPassword() (an
 * admin-forced reset mid sign-in). CognitoUser.changePassword() requires
 * the user object to actually have a hydrated, valid session attached
 * first - userPool.getCurrentUser() alone returns a "cold" instance with no
 * tokens loaded, so this calls getSession() to populate them before
 * attempting the change, same as getCurrentSession() does elsewhere in this
 * file.
 */
export function changePassword(oldPassword, newPassword) {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      reject(new Error('Not signed in.'));
      return;
    }

    user.getSession((sessionErr, session) => {
      if (sessionErr || !session || !session.isValid()) {
        reject(sessionErr || new Error('Your session has expired - please sign in again.'));
        return;
      }

      user.changePassword(oldPassword, newPassword, (changeErr) => {
        if (changeErr) {
          reject(changeErr);
          return;
        }
        resolve();
      });
    });
  });
}

/**
 * The dashboard API's Cognito JWT authorizer (API Gateway HTTP API) matches
 * the token's `aud` claim against the User Pool Client ID. Cognito ID
 * tokens carry `aud`; access tokens carry `client_id` instead and have no
 * `aud` claim at all - so this deliberately sends the ID token, not the
 * access token, as the Bearer credential. If you ever see 401s from the
 * dashboard API that look like a valid, unexpired token, this is the first
 * thing to check.
 */
export async function getIdToken() {
  const current = await getCurrentSession();
  if (!current) {
    return null;
  }
  return current.session.getIdToken().getJwtToken();
}
